import { Suspense } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import FallbackBoundary from 'components/FallbackBoundary';
import { RADIUS } from 'components/ui/tokens';
import { spring, useTourMotion } from 'sections/welcome-tour/motion';
import { postIcon } from '../postIcon';
import type { Post, PostMedia, PostTheme } from '../types';

const FILL_SX = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' } as const;

// React's `muted` prop sets only the property; mobile browsers want the attribute before they autoplay.
const muteClip = (video: HTMLVideoElement | null) => {
  if (!video) return;
  video.muted = true;
  video.setAttribute('muted', '');
};

/** No picture: the feature's icon, large, on a soft glow in the post's colours. */
const IconHero = ({ post, theme }: { post: Post; theme: PostTheme }) => {
  const { reduce } = useTourMotion();
  const Icon = postIcon(post);
  const grid = alpha(theme.text, 0.05);
  return (
    <Box
      sx={{
        ...FILL_SX,
        display: 'grid',
        placeItems: 'center',
        background: [
          `radial-gradient(90% 120% at 15% 0%, ${theme.glow}, transparent 55%)`,
          `radial-gradient(70% 90% at 100% 100%, ${alpha(theme.accent, 0.12)}, transparent 60%)`,
          `repeating-linear-gradient(0deg, transparent 0 23px, ${grid} 23px 24px)`,
          `repeating-linear-gradient(90deg, transparent 0 23px, ${grid} 23px 24px)`
        ].join(',')
      }}
    >
      <Box
        component={motion.div}
        initial={reduce ? false : { scale: 0.6, rotate: -10, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={spring}
        sx={{
          width: 88,
          height: 88,
          display: 'grid',
          placeItems: 'center',
          borderRadius: RADIUS.card,
          color: theme.accent,
          backgroundColor: alpha(theme.accent, 0.12),
          border: `1px solid ${alpha(theme.accent, 0.35)}`,
          boxShadow: `0 18px 40px -18px ${theme.glow}`,
          fontSize: 40,
          '& svg': { width: 40, height: 40 }
        }}
      >
        <Icon aria-hidden />
      </Box>
    </Box>
  );
};

/** A screen's picture, filling the space it is given: a screenshot, a clip, the feature's own animation, or the icon. */
const SpotlightHero = ({ post, media, theme }: { post: Post; media?: PostMedia; theme: PostTheme }) => {
  const { reduce } = useTourMotion();
  const fallback = <IconHero post={post} theme={theme} />;

  const content = (() => {
    if (!media) return fallback;
    if (media.kind === 'image') return <Box component="img" src={media.src} alt={media.alt} sx={FILL_SX} />;
    // People who turn off motion get the still frame; everyone else a muted clip that plays once.
    if (media.kind === 'clip') {
      return reduce ? (
        <Box component="img" src={media.poster} alt={media.alt} sx={FILL_SX} />
      ) : (
        <Box
          component="video"
          ref={muteClip}
          src={media.src}
          poster={media.poster}
          aria-label={media.alt}
          autoPlay
          playsInline
          sx={FILL_SX}
        />
      );
    }
    const Hero = media.component;
    return (
      <Box role="img" aria-label={media.alt} sx={FILL_SX}>
        {/* A chunk that fails to load (say, a tab open across a deploy) shows the icon, not an error page. */}
        <FallbackBoundary fallback={fallback}>
          <Suspense fallback={fallback}>
            <Hero />
          </Suspense>
        </FallbackBoundary>
      </Box>
    );
  })();

  return <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>{content}</Box>;
};

export default SpotlightHero;
